import ApiClient from 'api-client/ApiClient'
import ActionsSet from 'core/actions/ActionsSet'
import CustomAction from 'core/actions/CustomAction'
import ListAction from 'core/actions/ListAction'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import { noop } from 'utils/fp'

const { glance } = ApiClient.getInstance()

const imageMembersActions = ActionsSet.make<DataKeys.ImageMembers>({
  uniqueIdentifier: 'member_id',
  entityName: entityNamesByKey.ImageMembers,
  cacheKey: DataKeys.ImageMembers,
  indexBy: 'imageId',
})

// FR-001: List image members for a given image (GET /v2/images/{imageId}/members)
export const listImageMembersAction = imageMembersActions.add(
  new ListAction<DataKeys.ImageMembers, { imageId: string }>(async ({ imageId }) => {
    return glance.listImageMembers({ imageId })
  }),
)

// FR-001: Share an image with a project (POST /v2/images/{imageId}/members)
// FR-005: 409 Conflict is handled by callers (EditImageModal, AddImageModal) — not swallowed here
export const addImageMemberAction = imageMembersActions.add(
  new CustomAction<DataKeys.ImageMembers, { imageId: string; projectId: string }>(
    'addImageMember',
    async ({ imageId, projectId }) => {
      return glance.addImageMember({ imageId, projectId })
    },
    noop,
  ),
)

// FR-002: Revoke a project's access to an image (DELETE /v2/images/{imageId}/members/{memberId})
// FR-006: 403 Forbidden propagated to callers
export const removeImageMemberAction = imageMembersActions.add(
  new CustomAction<DataKeys.ImageMembers, { imageId: string; memberId: string }>(
    'removeImageMember',
    async ({ imageId, memberId }) => {
      return glance.removeImageMember({ imageId, memberId })
    },
    noop,
  ),
)

// FR-003: Consumer accepts a shared image (PUT /v2/images/{imageId}/members/{memberId} status=accepted)
export const acceptImageMemberAction = imageMembersActions.add(
  new CustomAction<DataKeys.ImageMembers, { imageId: string; memberId: string }>(
    'acceptImageMember',
    async ({ imageId, memberId }) => {
      return glance.updateImageMemberStatus({ imageId, memberId, status: 'accepted' })
    },
    noop,
  ),
)

// FR-009: Consumer denies a shared image via PUT with status=rejected
// Consumers MUST NOT use DELETE (owner-only endpoint, returns 403)
export const rejectImageMemberAction = imageMembersActions.add(
  new CustomAction<DataKeys.ImageMembers, { imageId: string; memberId: string }>(
    'rejectImageMember',
    async ({ imageId, memberId }) => {
      return glance.updateImageMemberStatus({ imageId, memberId, status: 'rejected' })
    },
    noop,
  ),
)

export const imageMembersSelector = createSharedSelector(
  getDataSelector<DataKeys.ImageMembers>(DataKeys.ImageMembers, 'imageId'),
  (members) => members,
)
